import { useState, useEffect, useRef } from "react";
import { Home, Loader2, MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import PropertyCard from "@/components/PropertyCard";
import PropertyFilters from "@/components/PropertyFilters";
import axios from "axios";
import { Link } from "react-router-dom";

const API = "/api";

// Interactive Map Component using Leaflet
const PropertyMap = ({ properties, onPropertyClick }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    // Load Leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Load Leaflet JS
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => initMap();
      document.head.appendChild(script);
    } else {
      initMap();
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapInstanceRef.current && window.L) {
      updateMarkers();
    }
  }, [properties]);

  const initMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const L = window.L;
    
    // Center on South of France (Aveyron region)
    const map = L.map(mapRef.current).setView([43.8, 2.5], 9);
    
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap'
    }).addTo(map);

    mapInstanceRef.current = map;
    updateMarkers();
  };

  const updateMarkers = () => {
    if (!mapInstanceRef.current || !window.L) return;

    const L = window.L;
    const map = mapInstanceRef.current;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // City coordinates (approximate)
    const cityCoords = {
      "Saint-Sernin-Sur-Rance": [43.8833, 2.6167],
      "Saint-Affrique": [43.9567, 2.8892],
      "Lacaune": [43.7083, 2.6917],
      "Combret": [43.8667, 2.5833],
      "Laval-Roquecezière": [43.8167, 2.5833],
      "Camarès": [43.8167, 2.8833],
      "Fayet": [43.7167, 2.6833],
      "Saint-Juéry": [43.9333, 2.2167],
      "Peux-Et-Couffouleux": [43.8667, 2.6500]
    };

    // Group properties by city
    const propertiesByCity = {};
    properties.forEach(prop => {
      const city = prop.city;
      if (!propertiesByCity[city]) {
        propertiesByCity[city] = [];
      }
      propertiesByCity[city].push(prop);
    });

    // Create markers for each city
    const bounds = [];
    Object.entries(propertiesByCity).forEach(([city, cityProperties]) => {
      const coords = cityCoords[city];
      if (!coords) return;

      bounds.push(coords);

      const customIcon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="
          background-color: #D62965;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          border: 3px solid white;
          box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        ">${cityProperties.length}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16]
      });

      const marker = L.marker(coords, { icon: customIcon }).addTo(map);
      
      // Popup with properties list
      const popupContent = `
        <div style="min-width: 200px; max-width: 280px;">
          <h3 style="font-weight: 600; font-size: 14px; margin-bottom: 8px; color: #0A0A0A;">${city}</h3>
          <p style="font-size: 12px; color: #52525B; margin-bottom: 8px;">${cityProperties.length} bien(s) disponible(s)</p>
          <div style="max-height: 200px; overflow-y: auto;">
            ${cityProperties.map(prop => `
              <a href="/bien/${prop.reference}" style="
                display: block;
                padding: 8px;
                margin-bottom: 4px;
                background: #f4f4f5;
                text-decoration: none;
                color: #0A0A0A;
                border-radius: 4px;
                font-size: 12px;
              ">
                <div style="font-weight: 500; margin-bottom: 2px;">${prop.title.substring(0, 40)}${prop.title.length > 40 ? '...' : ''}</div>
                <div style="color: #D62965; font-weight: 600;">${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(prop.price)}</div>
              </a>
            `).join('')}
          </div>
        </div>
      `;
      
      marker.bindPopup(popupContent);
      markersRef.current.push(marker);
    });

    // Fit bounds if we have markers
    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  return (
    <div 
      ref={mapRef} 
      className="w-full h-[400px] rounded-lg border border-zinc-200"
      data-testid="property-map"
    />
  );
};

export default function SalesPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [count, setCount] = useState(0);
  const [showMap, setShowMap] = useState(true);

  const fetchProperties = async (filterParams = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(response.data);
      setCount(response.data.length);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties(filters);
  }, []);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchProperties(newFilters);
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="sales-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#00B5AD] mb-3">
            Nos annonces
          </p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight font-light text-zinc-900">
            Biens à la <span className="font-medium">vente</span>
          </h1>
          <p className="mt-4 text-zinc-600 max-w-2xl">
            Découvrez notre sélection de biens immobiliers disponibles à la vente. 
            Utilisez les filtres pour affiner votre recherche.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-20 z-40 bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <PropertyFilters filters={filters} onFilterChange={handleFilterChange} />
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Results Count & Map Toggle */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-zinc-600" data-testid="results-count">
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement...
              </span>
            ) : (
              <span>
                <strong className="text-zinc-900">{count}</strong> bien{count > 1 ? 's' : ''} trouvé{count > 1 ? 's' : ''}
              </span>
            )}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMap(!showMap)}
            className="rounded-none border-zinc-200"
            data-testid="toggle-map-btn"
          >
            <MapPin className="h-4 w-4 mr-2" />
            {showMap ? "Masquer la carte" : "Afficher la carte"}
          </Button>
        </div>

        {/* Interactive Map */}
        {showMap && !loading && properties.length > 0 && (
          <div className="mb-8" data-testid="map-container">
            <PropertyMap properties={properties} />
            <p className="text-xs text-zinc-500 mt-2 text-center">
              Cliquez sur un marqueur pour voir les biens disponibles dans cette ville
            </p>
          </div>
        )}

        {/* Properties Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white border border-zinc-200">
                <div className="aspect-[4/3] skeleton" />
                <div className="p-5 space-y-3">
                  <div className="h-4 skeleton w-1/3" />
                  <div className="h-6 skeleton w-3/4" />
                  <div className="h-4 skeleton w-1/2" />
                  <div className="h-8 skeleton w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : properties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="properties-grid">
            {properties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16" data-testid="no-results">
            <Home className="h-16 w-16 mx-auto mb-4 text-zinc-300" />
            <h3 className="font-['Outfit'] text-xl font-medium text-zinc-900 mb-2">
              Aucun bien trouvé
            </h3>
            <p className="text-zinc-500 max-w-md mx-auto">
              Aucun bien ne correspond à vos critères de recherche. 
              Essayez de modifier vos filtres ou de réinitialiser la recherche.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
